/**
 * Nav gating for Roles & Access.
 *
 * Maps sidebar paths to the capability that governs them, so a module an
 * admin has switched off disappears from the menu instead of rendering a
 * page that loads and then 403s. An empty page reads as "the app is broken";
 * a missing menu entry reads as "not my job". Only the second is true.
 *
 * Deliberately path-keyed rather than annotating every menu item: the menu
 * is defined three times over (one arm per role) with nested submenus, and a
 * single mapping table can't drift between those copies.
 *
 * DEFAULT VISIBLE. A path with no entry here is never hidden — same
 * default-allow rule the backend registry uses, for the same reason.
 */

// Longest prefix wins, so '/admin/hr/payroll' beats '/admin/hr'.
const PATH_CAPABILITIES = [
  ['/admin/marketing',            'marketing.view'],
  ['/sales/lead-pool',            'leads.view'],
  ['/sales/reports',              'sales.reports'],
  ['/sales/collections',          'sales.reports'],
  ['/admin/discount/approvals',   'discount.approve'],
  ['/admin/discount/policy',      'discount.policy'],
  ['/sales/manager/leads',        'leads.view'],
  ['/sales/manager',              'leads.view'],
  ['/admin/leads',                'leads.view'],
  ['/admin/cases',                'cases.view'],
  ['/admin/pipeline',             'cases.view'],
  ['/admin/processing',           'cases.view'],
  ['/admin/clients',              'clients.view'],
  ['/admin/visa-programs',        'settings.manage'],
  ['/admin/document-library',     'documents.view'],
  ['/admin/accounting',           'accounting.view'],
  ['/admin/hr/job-applications',  'hr.recruitment.view'],
  ['/admin/hr/payroll',           'hr.payroll.view'],
  ['/admin/hr/attendance',        'hr.attendance.view'],
  ['/admin/hr/leaves',            'hr.leaves.view'],
  ['/admin/hr/policy',            'hr.policy.manage'],
  ['/admin/users',                'hr.staff.view'],
  ['/admin/complaints',           'complaints.view'],
  ['/admin/analytics',            'analytics.view'],
  ['/admin/autopilot',            'intelligence.view'],
  ['/legal',                      'legal.view'],
  ['/compliance',                 'compliance.view'],
  ['/admin/recycle-bin',          'recyclebin.view'],
  ['/admin/b2b-partners',         'b2b.manage'],
  ['/tasks',                      'tasks.view'],
].sort((a, b) => b[0].length - a[0].length);

export function capabilityForPath(path) {
  if (!path) return null;
  const clean = path.split('?')[0];
  const hit = PATH_CAPABILITIES.find(
    ([prefix]) => clean === prefix || clean.startsWith(`${prefix}/`),
  );
  return hit ? hit[1] : null;
}

/**
 * Drop menu entries the user's role has been denied.
 *
 * Mirrors applyVerticalToMenu's contract exactly: a parent whose children all
 * survive is kept as-is; a parent with SOME surviving children is kept and
 * re-pointed at the first visible child so the group link never lands on a
 * page the user can't open; a parent with no survivors and a denied own-path
 * is dropped. Returns the input array untouched when `can` is absent, so the
 * no-permissions path renders a byte-identical menu.
 */
export function applyPermissionsToMenu(items, can) {
  if (typeof can !== 'function') return items;
  const allowed = (path) => {
    const cap = capabilityForPath(path);
    return !cap || can(cap);
  };

  const out = [];
  for (const item of items) {
    if (item.submenu) {
      const submenu = item.submenu.filter((s) => allowed(s.path));
      if (submenu.length === 0 && !allowed(item.path)) continue;
      const next = { ...item, submenu };
      if (!allowed(item.path) && submenu.length > 0) next.path = submenu[0].path;
      out.push(next);
    } else {
      if (!allowed(item.path)) continue;
      out.push(item);
    }
  }
  return out;
}

export default applyPermissionsToMenu;
